import { createHTTPError } from '@gitbook/util-debug';

import { AuthenticationContentPermission, type AuthenticationTokenPayload } from './types';

const PERMISSIONS_SORTED = [
    AuthenticationContentPermission.READ,
    AuthenticationContentPermission.WRITE_BRANCH,
    AuthenticationContentPermission.WRITE,
    AuthenticationContentPermission.ADMIN,
];

/**
 * Returns the permission of a contentId from an authentication payload, or null if
 * there are no permissions for this contentId.
 */
export function getContentPermission(
    contentId: string,
    payload: AuthenticationTokenPayload
): AuthenticationContentPermission | null | undefined {
    const exactMatch = contentId in payload.content ? payload.content[contentId] : undefined;

    // Exact matches take precedence over all, skip the pattern matching and check straight away.
    if (exactMatch) {
        return exactMatch;
    }

    // If there's no exact match, check for glob patterns. Find the longest matching
    // glob pattern and assert content permissions against that.
    const patternMatch = findMatchingContentPermissionPattern(
        contentId,
        Object.keys(payload.content)
    );

    if (!patternMatch) {
        return null;
    }

    return payload.content[patternMatch];
}

/**
 * Ensure a request is authenticated and has the required level of permissions on a specific content.
 */
export function assertHasContentPermission(
    requiredPermission: AuthenticationContentPermission,
    contentId: string,
    payload: AuthenticationTokenPayload
) {
    const permission = getContentPermission(contentId, payload);

    if (!hasContentPermission(requiredPermission, permission)) {
        throw new createHTTPError.Forbidden(
            `Request is not authorized to access content with permission "${permission}", requires "${requiredPermission}"`
        );
    }
}

/**
 * From a list/iterator of content permission patterns, find the highest
 * precendence pattern that matches the contentId.
 *
 * Returns undefined if no patterns match the contentId.
 */
export function findMatchingContentPermissionPattern(
    contentId: string,
    patterns: IterableIterator<string> | string[]
): string | undefined {
    let longestMatchingPattern: string | undefined;

    for (const pattern of patterns) {
        if (!matchesGlob(pattern, contentId)) {
            continue;
        }

        longestMatchingPattern =
            !longestMatchingPattern || pattern.length > longestMatchingPattern.length
                ? pattern
                : longestMatchingPattern;
    }

    return longestMatchingPattern;
}

/**
 * Returns true if the given string is a content permission glob.
 */
export function isContentPermissionGlob(str: string): boolean {
    return str.endsWith('*');
}

/**
 * Return true if the given permission is at least as high as the required permission.
 */
function hasContentPermission(
    requiredPermission: AuthenticationContentPermission,
    permission: AuthenticationContentPermission | null | undefined
) {
    if (!permission) {
        return false;
    }

    if (PERMISSIONS_SORTED.indexOf(permission) < PERMISSIONS_SORTED.indexOf(requiredPermission)) {
        return false;
    }

    return true;
}

/**
 * Returns true if the given pattern is a glob and matches the string.
 */
function matchesGlob(pattern: string, str: string): boolean {
    if (!isContentPermissionGlob(pattern)) {
        return false;
    }

    const expr = pattern.replace(/\*$/, '.*');
    const re = new RegExp(`^${expr}\$`);

    return re.test(str);
}
